// ui/music.ts — optional background tracks, off by default.
//
// Extracted verbatim from an inline <script> in index.html. Track names here
// must stay in step with TRACKS in build.ts, which stages the .ogg encodes.

export {};

/* ---------------------------------------------------------------------------
 * Music — optional background tracks, off by default.
 *
 * SHUFFLE
 *   Tracks are grouped, not listed. town and shop are near-identical, so they
 *   share one slot: each pass through the playlist plays blossom, journey and
 *   exactly one of town/shop, in random order. Listing all four flat would
 *   make the town-ish sound half of everything you hear. Which of the pair
 *   plays alternates between passes rather than being re-rolled, so you can't
 *   get shop three cycles running.
 *
 * LAZY BY DESIGN
 *   Nothing is fetched until the toggle is switched on. That matters more
 *   than usual here: see the note on EXT below. It also sidesteps browser
 *   autoplay policy entirely on a first visit, because the toggle click *is*
 *   the user gesture the policy wants.
 *
 * ON RELOAD
 *   The on/off choice persists, but a saved "on" cannot autoplay — there has
 *   been no gesture yet in the new page. play() rejects, and we arm a one-shot
 *   listener so the music starts at the player's first click or keypress
 *   instead of the preference silently doing nothing.
 * ------------------------------------------------------------------------ */
(window as any).Music = (function () {
  'use strict';

  /* Ogg Vorbis, not the source .wav. Two reasons, and the second is fatal:
   *
   *   1. The four tracks run 8m32s — about 85 MB as PCM, ~6 MB as Vorbis.
   *   2. Cloudflare rejects any single static asset over 25 MiB, and
   *      blossom.wav alone is ~33 MB. `wrangler deploy` would fail outright
   *      with "Asset too large" rather than merely being slow.
   *
   * Produce them with:
   *   for f in music/*.wav; do
   *     ffmpeg -i "$f" -c:a libvorbis -q:a 4 -ac 2 "${f%.wav}.ogg"
   *   done
   *
   * Safari has supported Vorbis in <audio> since 16.4. For older iOS, also
   * emit .m4a (-c:a aac -b:a 128k) and choose between them with
   * canPlayType('audio/ogg; codecs="vorbis"'). */
  var EXT = '.ogg';

  /* Served from dist/music/, which build.ts populates — wrangler only
   * uploads ./dist, so files left in the project-root music/ folder are
   * invisible to the deploy and 404 at runtime. */
  var DIR = './music/';

  /* One entry per shuffle slot. Nested names share a slot. */
  var GROUPS = [['blossom'], ['journey'], ['town', 'shop']];

  var DEFAULT_VOL = 0.4;

  /* URL override: ?music=1|on|true|yes (or a bare ?music) starts enabled,
   * ?music=0|off|false|no forces it off. Either way it beats the saved
   * preference for this page load only and is never written back — see the
   * init block at the bottom. Anything unrecognised is ignored rather than
   * guessed at, so a typo falls through to localStorage. */
  function urlPref() {
    var v;
    try { v = new URLSearchParams(location.search).get('music'); }
    catch (e) { return null; }
    if (v === null) return null;
    v = v.trim().toLowerCase();
    if (v === '' || v === '1' || v === 'on' || v === 'true' || v === 'yes') return true;
    if (v === '0' || v === 'off' || v === 'false' || v === 'no') return false;
    return null;
  }

  var btn    = document.getElementById('btn-music') as HTMLButtonElement | null;
  var volRow = document.getElementById('music-row');
  var volEl  = document.getElementById('music-vol') as HTMLInputElement | null;
  if (!btn) return null;

  var audio = null;
  var enabled = false;
  var vol = DEFAULT_VOL;
  var order = [], pos = 0, lastGroup = -1;
  var turn = GROUPS.map(function () { return 0; });   /* per-group rotation */
  var failures = 0, fadeTimer = null, armed = false;

  function store(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function load(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }

  function t(key) { return window.I18N ? I18N.t(key) : key; }
  function colon() {
    var l = window.I18N ? I18N.locale : 'en';
    return l === 'fr' ? ' : ' : l === 'zh' ? '\uff1a' : ': ';
  }

  /* --- playlist ------------------------------------------------------- */
  function reshuffle() {
    order = GROUPS.map(function (_, i) { return i; });
    for (var i = order.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = order[i]; order[i] = order[j]; order[j] = tmp;
    }
    /* Without this, a group can land last in one pass and first in the next,
     * playing back to back across the seam. */
    if (order.length > 1 && order[0] === lastGroup) {
      var s = order[0]; order[0] = order[1]; order[1] = s;
    }
    pos = 0;
  }

  function pickNext() {
    if (pos >= order.length) reshuffle();
    var g = order[pos++];
    lastGroup = g;
    var names = GROUPS[g];
    var name = names[turn[g] % names.length];
    turn[g]++;
    return name;
  }

  /* --- audio ---------------------------------------------------------- */
  function ensureAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    audio.volume = vol;
    audio.addEventListener('ended', function () { failures = 0; playNext(); });
    audio.addEventListener('error', function () {
      /* A missing or undecodable file shouldn't kill playback — skip it. If
       * every track fails, the directory is wrong; stop rather than spin. */
      failures++;
      if (!enabled) return;
      if (failures >= GROUPS.length + 1) {
        console.warn('[Music] no track could be played. Check that build.ts ' +
                     'copied the .ogg files into dist/music/ — wrangler only ' +
                     'uploads ./dist, so a project-root music/ folder 404s.');
        setEnabled(false);
        return;
      }
      playNext();
    });
    return audio;
  }

  function tryPlay() {
    var p = ensureAudio().play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { armGesture(); });
    }
  }

  function playNext() {
    if (!enabled) return;
    ensureAudio().src = DIR + pickNext() + EXT;
    tryPlay();
  }

  /* Autoplay policy: a saved "on" has no gesture behind it on a fresh load,
   * so wait for the first real interaction. Passive-ish — this listener never
   * calls preventDefault or stops propagation, so map dragging is untouched. */
  function armGesture() {
    if (armed) return;
    armed = true;
    var go = function () {
      document.removeEventListener('pointerdown', go, true);
      document.removeEventListener('keydown', go, true);
      armed = false;
      if (!enabled) return;
      /* A saved "on" has already set a src before arming; ?music=1 has not,
       * and deliberately so — nothing downloads until this gesture. */
      if (audio && audio.src) tryPlay();
      else playNext();
    };
    document.addEventListener('pointerdown', go, true);
    document.addEventListener('keydown', go, true);
  }

  function fadeOutAndPause() {
    if (!audio) return;
    clearInterval(fadeTimer);
    var from = audio.volume, steps = 12, i = 0;
    fadeTimer = setInterval(function () {
      i++;
      audio.volume = Math.max(0, from * (1 - i / steps));
      if (i >= steps) {
        clearInterval(fadeTimer);
        audio.pause();
        audio.volume = vol;          /* restore for next time */
      }
    }, 25);
  }

  /* --- state ---------------------------------------------------------- */
  /* `persist` defaults to true; only an explicit false skips the write, so
   * every existing caller — the button, the all-tracks-failed bail-out, the
   * public API — keeps saving the choice as before. */
  function setEnabled(on, persist?: boolean) {
    enabled = !!on;
    if (persist !== false) store('music', enabled ? '1' : '0');
    render();
    if (!enabled) { fadeOutAndPause(); return; }
    clearInterval(fadeTimer);
    ensureAudio().volume = vol;
    if (audio.src) tryPlay();        /* resume where it left off */
    else playNext();
  }

  function setVolume(v) {
    vol = Math.max(0, Math.min(1, v));
    store('musicVol', String(Math.round(vol * 100)));
    if (audio) audio.volume = vol;
    if (volEl && Number(volEl.value) !== Math.round(vol * 100)) {
      volEl.value = String(Math.round(vol * 100));
    }
  }

  function render() {
    btn.textContent = t('music') + colon() + t(enabled ? 'on' : 'off');
    btn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    if (volRow) volRow.hidden = !enabled;
  }

  btn.addEventListener('click', function () { setEnabled(!enabled); });
  if (volEl) {
    volEl.addEventListener('input', function () { setVolume(Number(volEl.value) / 100); });
  }
  document.addEventListener('localechange', render);

  /* Don't play to an empty room. */
  document.addEventListener('visibilitychange', function () {
    if (!enabled || !audio) return;
    if (document.hidden) audio.pause();
    else tryPlay();
  });

  var savedVol = load('musicVol');
  if (savedVol !== null && savedVol !== '' && !isNaN(Number(savedVol))) {
    vol = Math.max(0, Math.min(1, Number(savedVol) / 100));
  }
  setVolume(vol);
  render();
  var forced = urlPref();
  if (forced === null) {
    if (load('music') === '1') setEnabled(true);
  } else if (forced) {
    /* Not setEnabled(true, false): that sets a src and starts the download
     * immediately, while play() is going to reject anyway for want of a
     * gesture. Mark it on, show the volume row, and let the first interaction
     * begin both the fetch and the playback. */
    enabled = true;
    render();
    armGesture();
  }
  /* ?music=0 needs no branch — the module starts off, and landing in the
   * else-if is what stops a saved '1' from applying. */

  return {
    get enabled() { return enabled; },
    get volume() { return vol; },
    setEnabled: setEnabled,
    setVolume: setVolume,
    skip: playNext
  };
})();
